import { config } from '../config';

/** Roll per kund, härledd av api:et vid varje anrop (spec kap 9). */
export type Roll = 'REVISIONSLEDARE' | 'REVISOR';

/**
 * Produktionsinriktning som verifieringen skopas på – "inriktning", inte
 * djurslag, eftersom framtida val inkluderar växt. v1: endast gris.
 */
export const INRIKTNINGAR = ['gris'] as const;
export type Inriktning = (typeof INRIKTNINGAR)[number];

export const INRIKTNING_LABEL: Record<Inriktning, string> = {
  gris: 'Gris',
};

export const isInriktning = (value: string): value is Inriktning =>
  (INRIKTNINGAR as readonly string[]).includes(value);

/** Avvikelsemodellerna (kravspec v4 5.3) – nycklarna kommer från backoffice-api. */
const MODELL_LABEL: Record<string, string> = {
  slaktgris: 'Slaktgris',
  smagris: 'Smågrisproduktion',
};
export const modellLabel = (modell: string) => MODELL_LABEL[modell] ?? modell;

/**
 * Tillståndsmaskin för urval (spec 6.4): öppet tills revisionsledaren
 * avpseudonymiserar, därefter låst permanent. Antal-steget syns som
 * `lastAntal` på urvalet, inte som status.
 */
export type UrvalStatus = 'PSEUDONYMISERAD' | 'AVPSEUDONYMISERAD_LAST';

/** En kund användaren har giltig åtkomst till, med roll, åtkomliga år och inriktningar. */
export interface KundAtkomst {
  kundId: string;
  /** Kundens namn från uppsättningen i backoffice; null tills det sparats. */
  kundNamn: string | null;
  roll: Roll;
  giltigFrom: string;
  giltigTom: string;
  atkomligaAr: number[];
  atkomligaInriktningar: Inriktning[];
  /** Läsuppdrag för kundens externa revisor: valt urval utan identitet (Ö21). */
  endastPseudonymiserat: boolean;
  /** AS-4231: dataskyddsvillkoren godkända för kunden – annars visas dialogen före revisionen. */
  dataskyddGodkant: boolean;
}

/** Svar på GET /me/access – driver kundval och årslista (spec flöde 6.1). */
export interface MyAccessResponse {
  epost: string;
  kunder: KundAtkomst[];
}

/** Kund + år + inriktning – revisionens skopning (ett urval per kombination). */
export interface RevisionScope {
  kundId: string;
  ar: number;
  inriktning: Inriktning;
}

/** Ett nyckeltal/kontrollpunkt – namn, typ och förväntat intervall. */
export interface RevisionMattDefinition {
  falt: string;
  visningsnamn: string;
  typ: 'nyckeltal' | 'kontrollpunkt';
  min: number | null;
  max: number | null;
}

/**
 * Måttet med detaljvyns förklaringstexter (kravspec v4 K6). Texterna
 * redigeras i backoffice (Verifiering › Metodparametrar) och läses live –
 * tomma tills beräkningsteamet lagt in dem.
 */
export interface RevisionMatt extends RevisionMattDefinition {
  enhet: string | null;
  verifieraSiffran: string | null;
  baserasPa: string[];
  omNyckeltalet: string | null;
  /** Beskrivning av normalintervallet – visas på raden under intervallet. */
  intervallBeskrivning: string | null;
}

/** Kolumn i revisionens tabeller: definitionen plus enhet när den är känd. */
export type RevisionKolumn = RevisionMattDefinition & { enhet: string | null };

/** Måttets läge hos en gård – aldrig värdet. */
export type MattStatus = 'under' | 'over' | 'inom' | 'saknas';

export interface RevisionMattUtfall extends RevisionMattDefinition {
  status: MattStatus;
}

export interface RevisionModell {
  modell: string;
  malnamn: string;
  matt: RevisionMatt[];
}

/**
 * Gårdens utfall i en modell: rang inom modellen, härlett avvikelsemått och
 * status för varje visbart mått (självbeskrivande även ur fryst urval).
 */
export interface RevisionModellRad {
  modell: string;
  rangordning: number;
  absDifferens: number;
  matt: RevisionMattUtfall[];
}

const tal = (n: number, decimaler = 3) =>
  n.toLocaleString('sv-SE', { maximumFractionDigits: decimaler });

/** Förväntat intervall som text: "8 – 40", "max 0,7", "min 2" eller "". */
export const intervallText = (m: RevisionMattDefinition) =>
  m.min !== null && m.max !== null
    ? `${tal(m.min)} – ${tal(m.max)}`
    : m.max !== null
      ? `max ${tal(m.max)}`
      : m.min !== null
        ? `min ${tal(m.min)}`
        : '';

/** Avvikelsemåttet (|beräknat − prediktion|) med två decimaler. */
export const avvikelseText = (absDifferens: number) => tal(absDifferens, 2);

export const MATT_STATUS_TEXT: Record<MattStatus, string> = {
  over: 'Över förväntat intervall',
  under: 'Under förväntat intervall',
  inom: 'Inom förväntat intervall',
  saknas: 'Värde saknas',
};

/**
 * Kolumnerna för en modells tabell: unionen av måtten i gårdarnas egna rader
 * (i radernas ordning), kompletterad med svarets definitioner när raderna
 * inte har några. Raderna vinner: ett låst urval visar de intervall gårdarna
 * bedömdes mot, inte dagens (v4 C.7), och definitionerna kan saknas när
 * backoffice-api inte svarat.
 */
export function mattKolumner(
  gardar: RevisionGard[],
  modell: string,
  definierade?: RevisionMatt[],
): RevisionKolumn[] {
  // Enheten finns bara i de live-lästa definitionerna – hämtas därifrån
  // även när kolumnerna byggs ur gårdarnas (frysta) rader.
  const enhetFor = new Map(definierade?.map((m) => [m.falt, m.enhet]));
  const perFalt = new Map<string, RevisionKolumn>();
  for (const gard of gardar) {
    for (const utfall of modellRad(gard, modell)?.matt ?? []) {
      if (!perFalt.has(utfall.falt)) {
        const { falt, visningsnamn, typ, min, max } = utfall;
        perFalt.set(falt, {
          falt,
          visningsnamn,
          typ,
          min,
          max,
          enhet: enhetFor.get(falt) ?? null,
        });
      }
    }
  }
  if (perFalt.size === 0 && definierade) {
    return definierade;
  }
  return [...perFalt.values()];
}

export interface RevisionIdentitet {
  gardsnamn: string | null;
  orgnr: string | null;
  /** AS-4226: företaget var borttaget när identiteten hämtades – orgnr saknas därför. */
  foretagBorttaget: boolean;
}

/**
 * En gård i revisionen – pseudonym tills gården avpseudonymiserats. Listorna
 * visas per modell: rang och avvikelser ligger i `modeller`, och en gård med
 * båda produktionsgrenarna finns i båda listorna med samma nummer. Tom
 * `modeller` = ingen modell kan bedöma gården.
 */
export interface RevisionGard {
  nummer: number;
  modeller: RevisionModellRad[];
  vald: boolean;
  identitet?: RevisionIdentitet;
}

/** Rubrik för en gård: identitet när den finns, annars pseudonymnumret. */
export const gardTitel = (gard: RevisionGard) =>
  gard.identitet?.gardsnamn ?? `Gård ${gard.nummer}`;

/** Gårdens rad i en given modell, om modellen bedömer gården. */
export const modellRad = (gard: RevisionGard, modell: string) =>
  gard.modeller.find((m) => m.modell === modell);

/** En sektion i revisionens listor: en per modell, sist "Utan bedömning". */
export interface GardSektion {
  /** Modellnyckel, eller null för gårdar som ingen modell bedömer. */
  modell: string | null;
  titel: string;
  gardar: RevisionGard[];
}

/**
 * Grupperar gårdarna per modell (Ö19b): varje modells lista sorterad efter
 * modellens rang, och sist de gårdar som saknar underlag. Modellordningen
 * tas från listsvaret när den finns, annars ur gårdarnas egna rader.
 */
export function grupperaPerModell(
  gardar: RevisionGard[],
  modeller?: { modell: string }[],
): GardSektion[] {
  const nycklar =
    modeller?.map((m) => m.modell) ??
    [...new Set(gardar.flatMap((g) => g.modeller.map((m) => m.modell)))];
  const perModell: GardSektion[] = nycklar.map((modell) => ({
    modell,
    titel: modellLabel(modell),
    gardar: gardar
      .filter((g) => modellRad(g, modell))
      .sort(
        (a, b) =>
          modellRad(a, modell)!.rangordning - modellRad(b, modell)!.rangordning,
      ),
  }));
  const utanBedomning = gardar
    .filter((g) => g.modeller.length === 0)
    .sort((a, b) => a.nummer - b.nummer);
  return [
    ...perModell,
    { modell: null, titel: 'Utan bedömning', gardar: utanBedomning },
  ];
}

/** Revisionsledarens lista (GET /revision/list). */
export interface RevisionListResponse {
  urvalId: string;
  status: UrvalStatus;
  roll: Roll;
  antalGardar: number;
  lastAntal: number | null;
  modeller: RevisionModell[];
  gardar: RevisionGard[];
}

/** De avpseudonymiserade gårdarna (GET /revision/revealed) – revisorernas vy. */
export interface RevisionRevealedResponse {
  urvalId: string | null;
  status: UrvalStatus | null;
  roll: Roll;
  lastAntal: number | null;
  /** False i ett pseudonymiserat läsuppdrag (Ö21): raderna saknar identitet. */
  identitetVisas: boolean;
  /** Måttdefinitioner med förklaringstexter, live (inte frysta i urvalet). */
  modeller: RevisionModell[];
  gardar: RevisionGard[];
}

export interface RevisionAntalResponse {
  urvalId: string;
  status: UrvalStatus;
  lastAntal: number;
  antalGardar: number;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Digs the human-readable message out of an error body. Nest's error object
 * is either a string, a string[] (class-validator) or `{ message, error,
 * statusCode }` with the same variants one level down.
 */
function felmeddelande(json: any): string | undefined {
  for (const m of [json?.message, json?.message?.message]) {
    if (typeof m === 'string' && m) {
      return m;
    }
    if (Array.isArray(m) && m.length > 0) {
      return m.join(', ');
    }
  }
  return undefined;
}

/**
 * Calls agrosphere-verifiering-api. Throws ApiError with the server's
 * Swedish message – 403/409-svaren bär affärsreglerna (15-dagarsregeln,
 * rollkrav, låst urval) och visas som de är.
 */
async function apiFetch<T>(
  accessToken: string,
  method: 'GET' | 'POST' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`${config.API_URL}/${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: body !== undefined ? JSON.stringify(body) : null,
  });

  if (res.status === 204) {
    return undefined as T;
  }

  let json: any;
  try {
    json = await res.json();
  } catch {
    json = undefined;
  }

  if (!res.ok) {
    const message = felmeddelande(json) ?? `Något gick fel (${res.status})`;
    throw new ApiError(message, res.status);
  }

  return json as T;
}

const scopeQuery = (scope: RevisionScope) =>
  `kundId=${encodeURIComponent(scope.kundId)}&ar=${scope.ar}&inriktning=${scope.inriktning}`;

export const verifieringApi = {
  getMyAccess: (token: string) =>
    apiFetch<MyAccessResponse>(token, 'GET', 'me/access'),

  /** Godkännande av att ingen data får sparas utanför applikationen, per kund (AS-4231). */
  godkannDataskydd: (token: string, kundId: string) =>
    apiFetch<{ kundId: string; godkandAt: string }>(
      token,
      'POST',
      'me/dataskydd-godkannande',
      { kundId },
    ),

  /** Revisionsledarens rangordnade, pseudonymiserade lista (kräver revisionsledare). */
  getRevisionList: (token: string, scope: RevisionScope) =>
    apiFetch<RevisionListResponse>(
      token,
      'GET',
      `revision/list?${scopeQuery(scope)}`,
    ),

  /** Antal-steget: antalet låses när det angetts (kravspec v4 C.3). */
  setRevisionAntal: (token: string, input: RevisionScope & { antal: number }) =>
    apiFetch<RevisionAntalResponse>(token, 'POST', 'revision/antal', input),

  /** Avpseudonymisering av exakt det låsta antalet gårdar – låser permanent. */
  revealRevision: (
    token: string,
    input: RevisionScope & { valdaNummer: number[] },
  ) =>
    apiFetch<RevisionRevealedResponse>(token, 'POST', 'revision/reveal', input),

  /** De avpseudonymiserade gårdarna – båda rollerna. */
  getRevisionRevealed: (token: string, scope: RevisionScope) =>
    apiFetch<RevisionRevealedResponse>(
      token,
      'GET',
      `revision/revealed?${scopeQuery(scope)}`,
    ),
};
